import { useContext } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/constants';
import i18nContext from 'components/i18n-context';
import { BACKEND_MODULE_TYPES } from 'components/common/constants';
import { PhysicalCardForm } from './CardCreationForms/PhysicalCardForm';
import { VirtualCardForm } from './CardCreationForms/VirtualCardForm';
import Loader from 'components/common/Loader';
import PopUpSuccessScheme from 'components/common/PopUpScheme/PopUpSuccessScheme';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import { amountFormattedValue } from 'services/utils';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import '../../Cards.scss';

const authorizedPersonOptions = [
  {
    key: 'Name Surname 1',
    value: 'Name Surname 1'
  },
  {
    key: 'Name Surname 2',
    value: 'Name Surname 2'
  },
  {
    key: 'Name Surname 3',
    value: 'Name Surname 3'
  },
  {
    key: 'Name Surname 4',
    value: 'Name Surname 4'
  }
];

const CreateNewCardForm = ({ customerNumber, accountDetails, customerType, cardsStore, paymentStore }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const i18n = useContext(i18nContext);

  const clearError = () => cardsStore.clearError();
  const setIsShowingInternalActionConfirmation = (status) => cardsStore.setIsShowingInternalActionConfirmation(status);
  const sendCreateCardRequest = (data) => cardsStore.sendCreateCardRequest(customerNumber, data);
  const sendConfirmCardCreationCode = (code) => cardsStore.sendConfirmCardCreationCode(customerNumber, code);
  const resendSecurityCode = () => cardsStore.resendSecurityCode();
  const onCloseSuccessPopUp = () => {
    cardsStore.setIsCardCreated(false);
    navigate('/cards');
  };

  const getAvailableBalance = () =>
    paymentStore?.currentAccount &&
    i18n.getMessage('sendMoney.topLabel', {
      available:
        paymentStore?.currentAccount.availableDetails?.displayAmount ||
        amountFormattedValue(paymentStore?.currentAccount.available),
      currency: paymentStore?.currentAccount.currency
    });

  const accountsOptions = cardsStore?.accounts.map((account) => {
    return {
      key: account.wallet_number,
      value: account.wallet_number
    };
  });

  const handleCloseInternalActionConfirmation = () => {
    setIsShowingInternalActionConfirmation(false);
  };

  return (
    <NewContainer className={'content-card-wrapper'} header={i18n.getMessage('container.cards')} isBackButton={true}>
      {!cardsStore.isInitialized ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : location.pathname === ROUTE_PATHS.CARDS_CREATE_VIRTUAL ? (
        <VirtualCardForm
          customerType={customerType}
          userFullName={`${accountDetails?.name} ${accountDetails?.surname}`}
          getAvailableBalance={getAvailableBalance}
          accountsOptions={accountsOptions}
          authorizedPersonOptions={authorizedPersonOptions}
          openSuccessPopUp={cardsStore.setIsCardCreated}
          sendCreateCardRequest={sendCreateCardRequest}
          isShowingInternalActionConfirmation={cardsStore.isShowingInternalActionConfirmation}
          setIsShowingInternalActionConfirmation={setIsShowingInternalActionConfirmation}
          resendSecurityCode={resendSecurityCode}
          error={cardsStore.temporaryError || cardsStore.error}
          clearError={clearError}
          dashboardSelectedAccountNumber={cardsStore.selectedAccount?.wallet_number}
        />
      ) : location.pathname === ROUTE_PATHS.CARDS_CREATE_PHYSICAL ? (
        <PhysicalCardForm />
      ) : null}

      <PopUp
        show={cardsStore.isCardCreated || cardsStore.isShowingInternalActionConfirmation}
        onClose={
          cardsStore.isShowingInternalActionConfirmation ? handleCloseInternalActionConfirmation : onCloseSuccessPopUp
        }
        alignOnCenter={true}
      >
        {cardsStore.isCardCreated && (
          <PopUpSuccessScheme
            onClose={onCloseSuccessPopUp}
            message={i18n.getMessage('popUp.success.virtualCardCreated')}
          />
        )}

        {cardsStore.isShowingInternalActionConfirmation && (
          <PopUpInternalActionConfirmationScheme
            isLoading={cardsStore.isLoading}
            isShowSwitchConfirmationMethodScreen={cardsStore.isShowSwitchConfirmationMethodScreen}
            title={i18n.getMessage('cards.popUp.actionConfirmation.title')}
            confirmButtonLabel={i18n.getMessage('cards.popUp.actionConfirmation.button.confirm')}
            onClose={handleCloseInternalActionConfirmation}
            onConfirm={(securityCode) => sendConfirmCardCreationCode(securityCode)}
            error={cardsStore.error}
            clearError={() => cardsStore.clearError()}
            resendSecurityCode={() => cardsStore.resendSecurityCode()}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              cardsStore.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={cardsStore.forceSwitchToAnotherConfirmationType}
            confirmationId={cardsStore.confirmationId}
            module={BACKEND_MODULE_TYPES.PAYMENT}
          />
        )}
      </PopUp>
    </NewContainer>
  );
};

CreateNewCardForm.propTypes = {
  customerNumber: PropTypes.string,
  accountDetails: PropTypes.object,
  customerType: PropTypes.string,
  cardsStore: MobXPropTypes.observableObject,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  accountDetails: stores.userStore.userData.account?.details,
  customerType: stores.userStore.userData.account?.type,
  cardsStore: stores.cardsStore,
  paymentStore: stores.paymentStore
}))(observer(CreateNewCardForm));
